// src/layouts/DashboardLayout.js
import React, { useEffect, useRef } from 'react'
import Sidebar from '../Sidebar'
import Navbar from '../Navbar'
import { getAuth, signOut } from 'firebase/auth'
import { db } from '../../firebaseConfig'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

export default function DashboardLayout({
  children,
  activeMenu,
  setActiveMenu,
  onLogout,
  user,
}) {
  const auth = getAuth()
  const idleTimerRef = useRef(null)
  const devtoolsTriggeredRef = useRef(false)

  // helper: log security event to Firestore
  async function logSecurityEvent(action, detail = '') {
    try {
      await addDoc(collection(db, 'security_logs'), {
        uid: user?.uid || null,
        email: user?.email || null,
        role: user?.role || null,
        action,
        detail,
        userAgent: navigator.userAgent,
        page: window.location.pathname,
        ts: serverTimestamp(),
      })
      // optionally you could call an HTTP Cloud Function here to push FCM
    } catch (err) {
      console.error('Failed to write security log:', err)
    }
  }

  // ======================================================
  // 1) AUTO LOGOUT - Idle detection (default 10 minutes)
  // ======================================================
  useEffect(() => {
    const AUTO_LOGOUT_MINUTES = 10 // change here if needed

    const resetTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        // if no user, nothing to do
        if (!auth.currentUser) return
        // sign out and redirect
        signOut(auth)
          .then(() => {
            alert('Anda otomatis logout karena tidak aktif.')
            window.location.href = '/login'
          })
          .catch(console.error)
      }, AUTO_LOGOUT_MINUTES * 60 * 1000)
    }

    const activityEvents = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'touchstart',
      'touchmove',
    ]
    activityEvents.forEach((ev) => window.addEventListener(ev, resetTimer))
    resetTimer()

    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, resetTimer))
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ======================================================
  // 2) DEVTOOLS DETECTION -> auto logout for non-super_admin
  // ======================================================
  useEffect(() => {
    if (!user) return
    if (user.role === 'super_admin') return // allow for super_admin

    let intervalId = null
    let lastCheck = performance.now()

    const detectDevTools = async () => {
      try {
        // method A: check difference between outer and inner size (when docked)
        const threshold = 160
        const devtoolsOpened =
          window.outerWidth - window.innerWidth > threshold ||
          window.outerHeight - window.innerHeight > threshold

        // method B: debugger detection by timing
        const t0 = performance.now()
        // eslint-disable-next-line no-debugger
        debugger
        const dt = performance.now() - t0
        const debuggerDetected = dt > 200 // if paused by debugger, dt spikes

        if (devtoolsOpened || debuggerDetected) {
          if (!devtoolsTriggeredRef.current) {
            devtoolsTriggeredRef.current = true
            await logSecurityEvent(
              'devtools_detected',
              `outer-inner:${window.outerWidth - window.innerWidth}, dt:${dt}`
            )
            // Auto logout non-super_admin
            signOut(auth)
              .then(() => {
                alert(
                  'Akses diblokir: Developer Tools terdeteksi. Silakan login kembali.'
                )
                window.location.href = '/login'
              })
              .catch(console.error)
          }
        }
      } catch (err) {
        console.error('DevTools detection error', err)
      } finally {
        lastCheck = performance.now()
      }
    }

    intervalId = setInterval(detectDevTools, 1000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [user, auth])

  // ======================================================
  // 3) ANTI-INSPECT (blokir klik kanan + hotkeys)
  // ======================================================
  useEffect(() => {
    if (!user) return

    // super_admin has full access
    if (user.role === 'super_admin') return

    const onContextMenu = (e) => {
      e.preventDefault()
      logSecurityEvent(
        'contextmenu_blocked',
        'right-click blocked by anti-inspect'
      )
      // lightweight UX feedback
      // small, non-blocking notice instead of alert to avoid abuse
      try {
        const el = document.createElement('div')
        el.innerText = 'Action blocked'
        el.style.position = 'fixed'
        el.style.bottom = '20px'
        el.style.right = '20px'
        el.style.background = '#b91c1c'
        el.style.color = 'white'
        el.style.padding = '6px 10px'
        el.style.borderRadius = '6px'
        el.style.zIndex = '99999'
        document.body.appendChild(el)
        setTimeout(() => el.remove(), 1600)
      } catch (e) {}
    }

    const onKeyDown = (e) => {
      // block F12, Ctrl+Shift+I / J / C, Ctrl+U, Ctrl+S (optional)
      const isF12 = e.key === 'F12' || e.keyCode === 123
      const ctrlShiftI =
        e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i')
      const ctrlShiftJ =
        e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j')
      const ctrlShiftC =
        e.ctrlKey && e.shiftKey && (e.key === 'C' || e.key === 'c')
      const ctrlU = e.ctrlKey && (e.key === 'u' || e.key === 'U')
      const metaU = e.metaKey && (e.key === 'u' || e.key === 'U') // mac
      const ctrlS = e.ctrlKey && (e.key === 's' || e.key === 'S')

      if (
        isF12 ||
        ctrlShiftI ||
        ctrlShiftJ ||
        ctrlShiftC ||
        ctrlU ||
        metaU ||
        ctrlS
      ) {
        e.preventDefault()
        e.stopPropagation()
        logSecurityEvent(
          'hotkey_blocked',
          `key:${e.key} ctrl:${e.ctrlKey} shift:${e.shiftKey} meta:${e.metaKey}`
        )
        // optionally force logout
        signOut(auth).then(() => {
          alert('Aksi tidak diizinkan. Anda akan diarahkan ke login.')
          window.location.href = '/login'
        })
      }

      // PrintScreen detection (attempt)
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        e.preventDefault()
        logSecurityEvent('printscreen_detected', 'PrintScreen pressed')
        // show brief notice
        try {
          const el = document.createElement('div')
          el.innerText = 'Screenshot attempt detected'
          el.style.position = 'fixed'
          el.style.bottom = '20px'
          el.style.left = '20px'
          el.style.background = '#b91c1c'
          el.style.color = 'white'
          el.style.padding = '6px 10px'
          el.style.borderRadius = '6px'
          el.style.zIndex = '99999'
          document.body.appendChild(el)
          setTimeout(() => el.remove(), 1600)
        } catch (e) {}
      }
    }

    window.addEventListener('contextmenu', onContextMenu)
    window.addEventListener('keydown', onKeyDown, { capture: true })

    return () => {
      window.removeEventListener('contextmenu', onContextMenu)
      window.removeEventListener('keydown', onKeyDown, { capture: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // ======================================================
  // 4) ANTI-SCREENSHOT: visibilitychange / blur heuristics
  // ======================================================
  useEffect(() => {
    if (!user) return
    if (user.role === 'super_admin') return

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // user switched tab / minimized — suspicious but not conclusive
        logSecurityEvent('visibility_hidden', 'document.visibilityState hidden')
      } else if (document.visibilityState === 'visible') {
        // returned
        logSecurityEvent(
          'visibility_visible',
          'document.visibilityState visible'
        )
      }
    }

    const onBlur = () => {
      // lost focus, possibly screenshot tool / switching apps
      logSecurityEvent('window_blur', 'window blur detected')
    }

    window.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('blur', onBlur)

    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('blur', onBlur)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // ======================================================
  // Render layout
  // ======================================================
  return (
    <div className="flex bg-gray-950 text-gray-200 min-h-screen">
      <Sidebar
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        onLogout={onLogout}
        user={user}
      />

      <div className="flex-1 flex flex-col">
        <Navbar userEmail={user?.email || ''} />

        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>

        <footer className="text-center text-gray-600 text-xs py-3 border-t border-gray-800 bg-gray-900">
          © {new Date().getFullYear()} Zimo Admin Dashboard — All rights
          reserved.
        </footer>
      </div>
    </div>
  )
}
